import React, { useState } from 'react';
import { Eye } from 'lucide-react';
import Card from '../../Components/Ui/Card';
import Button from '../../Components/Ui/Button';
import Pagination from '../../Components/Ui/Pagination';
import FilterBar from '../../features/Seller/Catalog/FilterBar';
import ProductTable from '../../features/Seller/Catalog/ProductTable';
import ConfirmDeleteModal from '../../Components/Ui/ConfirmDeleteModal';
import toastService from '../../services/toastService';
import Loader from '../../Components/Ui/Loader';

import { useNavigate } from 'react-router-dom';
import {
  useGetSellerCatalogQuery,
  useDeleteCatalogItemMutation,
  useUpdateCatalogItemMutation,
} from '../../app/services/catalogApi';
import { useGetSellerBrandsQuery } from '../../app/services/productApi';
import { useSelector } from 'react-redux';

const Catalog = () => {
  const { user } = useSelector((state) => state.userInfo);
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);

  const itemsPerPage = 5;

  const {
    data: catalogData,
    isLoading,
    isError,
    refetch,
  } = useGetSellerCatalogQuery(
    {
      sellerId: user?.sellerId || user?.userId || user?._id,
      page: currentPage,
      limit: itemsPerPage,
      category: categoryFilter,
      search: searchQuery,
      brand: brandFilter,
    },
    { refetchOnMountOrArgChange: true },
  );

  const [deleteCatalogItem] = useDeleteCatalogItemMutation();
  const [updateCatalogItem] = useUpdateCatalogItemMutation();

  const { data: brandsData, isLoading: brandsLoading } = useGetSellerBrandsQuery(
    user?.sellerId || user?.userId || user?._id,
  );

  const catalogItems = catalogData?.data || [];
  const pagination = catalogData?.pagination || {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  };

  const handleEditClick = (product) => {
    navigate(`/seller/catalog/edit/${product._id}`, {
      state: { product: product.productDetails },
    });
  };

  const handleDeleteClick = (productId) => {
    setSelectedProductId(productId);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteCatalogItem({ id: selectedProductId, permanent: true }).unwrap();
      toastService.success('Product removed from catalog successfully!');
      refetch();
    } catch (error) {
      toastService.error('Failed to remove product from catalog');
      console.error('Delete error:', error);
    } finally {
      setShowConfirmModal(false);
    }
  };

  const handleToggleStatus = async (product) => {
    const newStatus = product.status === 'active' ? 'draft' : 'active';
    try {
      await updateCatalogItem({ id: product._id, status: newStatus }).unwrap();
      toastService.success(`Product status updated to ${newStatus}`);
      refetch();
    } catch (error) {
      toastService.error('Failed to update product status');
      console.error('Toggle status error:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-10">
        <p className="text-red-500 mb-4">Failed to load catalog</p>
        <Button onClick={refetch} variant="outline">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full space-y-5 px-2 sm:px-4">
      {/* HEADER */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg sm:text-xl xl:text-2xl font-bold">My Catalog</h2>

        <Button
          size="sm"
          onClick={() => navigate('/seller/brand-catalog')}
          className="w-full sm:w-auto"
        >
          <Eye size={16} />
          View Brand Catalog
        </Button>
      </div>

      {/* CARD */}
      <Card>
        <FilterBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          categoryFilter={categoryFilter}
          setCategoryFilter={setCategoryFilter}
          brandFilter={brandFilter}
          setBrandFilter={setBrandFilter}
          brands={brandsData?.data}
          brandsLoading={brandsLoading}
          user={user}
        />

        {catalogItems.length === 0 ? (
          <div className="text-center py-10 text-sm tertiary-text-color">
            No products added to catalog
          </div>
        ) : (
          <>
            {/* TABLE WRAPPER */}
            <div className="w-full overflow-x-auto mt-4">
              <ProductTable
                products={catalogItems}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                onToggleStatus={handleToggleStatus}
              />
            </div>
            {/* PAGINATION */}
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              {/* Showing text — TOP on mobile */}
              <p className="text-xs sm:text-sm tertiary-text-color order-1 sm:order-none">
                Showing {catalogItems.length} of {pagination.totalItems} product
              </p>

              {/* Pagination — BOTTOM on mobile */}
              <div className="order-2 sm:order-none self-center sm:self-auto">
                <Pagination
                  currentPage={currentPage}
                  totalPages={pagination.totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            </div>
          </>
        )}
      </Card>

      {showConfirmModal && (
        <ConfirmDeleteModal
          onConfirm={confirmDelete}
          onCancel={() => setShowConfirmModal(false)}
          title="Remove from Catalog"
          message="Are you sure you want to remove this product from your catalog?"
        />
      )}
    </div>
  );
};

export default Catalog;
